import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../../context/AuthContext';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { walletAPI, paymentErrorText, serverMessage } from '../../services/walletApi';
import { openCheckout } from '../../services/payments/razorpayCheckout';
import { isCheckoutCancelled } from '../../services/payments/razorpayOptions';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Toast } from '../common/Toast';
import { Logger } from '../../utils/logger';
import {
  walletForChild,
  entryDetails,
  stamp,
  topUpChoices,
  topUpProblem,
  walletKindLabel,
  walletMoney,
  walletStatusChip,
  walletStatusNotice,
} from '../../utils/walletFormat';
import type { MyWallets, PayAndTopUp, Wallet, WalletEntry, WalletStatement } from '../../types/wallet';
import { BusyOverlay, Notice, Segmented } from './WalletBits';
import { PayAndTopUpContent } from './PayAndTopUpContent';
import { BalanceCard as KitBalanceCard, tokens } from '@shared/commerce';

/**
 * The parent's Wallet screen, shared by the web and native builds.
 *
 * One card per child's wallet (GET /wallet/my), opened on the selected child's
 * own; its balance, Payer ID, state, statement, unpaid bills and cards, with
 * "Block" for a lost card. "Top up" pays online only where the school has a
 * payment gateway; elsewhere the screen says to pay at the school office and
 * quote the Payer ID - the same sentences as the web parent portal.
 * "Pay & top up" is the second tab (fees, wallet bills and top-ups in one go).
 */

type Tab = 'wallet' | 'pay';

export function WalletContent({ initialTab = 'wallet' }: { initialTab?: 'wallet' | 'pay' } = {}) {
  const { students, selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [tab, setTab] = useState<Tab>(initialTab);
  // Opened from the home screen's Pay now: land on the Pay tab.
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const [mine, setMine] = useState<MyWallets | null>(null);
  const [limits, setLimits] = useState<PayAndTopUp | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statement, setStatement] = useState<WalletStatement | null>(null);
  const [statementError, setStatementError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [topUpFor, setTopUpFor] = useState<Wallet | null>(null);
  const [blockCardId, setBlockCardId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const load = useCallback(async () => {
    try {
      const [all, pay] = await Promise.all([
        walletAPI.getMyWallets(),
        // The top-up limits live on the Pay & top up read; a school without it
        // still shows wallets.
        walletAPI.getPayAndTopUp().catch(() => null),
      ]);
      setMine(all);
      setLimits(pay);
      setLoadError(null);
    } catch (err) {
      Logger.error('Wallet', 'Failed to load wallets', err);
      setLoadError(serverMessage(err, 'The wallets could not be loaded. Pull to refresh or try again later.'));
    }
  }, []);

  // Always the child chosen at the top of the app: switching child there switches the wallet here.
  const wallet = mine ? walletForChild(mine.wallets, selectedStudent?.id) : null;
  const openId = wallet?.id ?? null;
  const childName = selectedStudent?.name ?? 'Your child';

  const loadStatement = useCallback(async (id: string) => {
    try {
      setStatementError(null);
      setStatement(await walletAPI.getStatement(id));
    } catch (err) {
      Logger.error('Wallet', 'Failed to load statement', err);
      setStatementError(serverMessage(err, 'The statement could not be loaded.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setStatement(null);
    if (openId) { loadStatement(openId); }
  }, [openId, loadStatement]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    if (openId) { await loadStatement(openId); }
    setRefreshing(false);
  };

  const blockCard = async () => {
    if (!blockCardId) { return; }
    const id = blockCardId;
    setBlockCardId(null);
    setBusy(true);
    try {
      await walletAPI.blockCard(id, 'Reported lost by the family');
      setToast({ message: 'Card blocked.', type: 'success' });
      if (openId) { await loadStatement(openId); }
    } catch (err) {
      setToast({ message: serverMessage(err, 'The card could not be blocked.'), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const minTopUp = limits?.minTopUp ?? statement?.minTopUp ?? 0;
  const maxBalance = limits?.maxBalance ?? statement?.maxBalance ?? 0;

  const body = (() => {
    if (tab === 'pay') {
      return <PayAndTopUpContent onPaid={() => { load(); if (openId) { loadStatement(openId); } }} />;
    }
    if (!mine) {
      return loadError
        ? <Notice tone="error">{loadError}</Notice>
        : <LoadingSpinner />;
    }
    if (mine.wallets.length === 0) {
      return (
        <View style={styles.empty}>
          <View style={styles.emptyIcon}><FeatherIcon name="credit-card" size={22} color={colors.primary} /></View>
          <Text style={styles.emptyTitle}>
            {mine.enabled ? 'There is no wallet linked to your account.' : 'The school has not switched wallets on yet.'}
          </Text>
          {mine.enabled && <Text style={styles.emptySub}>Ask the school office to open one.</Text>}
        </View>
      );
    }

    return (
      <>
        {students.length > 1 && (
          <Text style={styles.forChild} testID="wallet-child">
            {`For ${childName}, the child chosen at the top. To see another child's wallet, choose them there.`}
          </Text>
        )}

        {!wallet && (
          <View style={styles.empty} testID="wallet-none-for-child">
            <View style={styles.emptyIcon}><FeatherIcon name="credit-card" size={22} color={colors.primary} /></View>
            <Text style={styles.emptyTitle}>{`${childName} has no wallet yet.`}</Text>
            <Text style={styles.emptySub}>Ask the school office to open one.</Text>
          </View>
        )}

        {wallet && (
          <WalletBalance
            wallet={wallet}
            canTopUp={mine.onlineTopUp && wallet.status !== 'Closed'}
            onTopUp={() => setTopUpFor(wallet)}
          />
        )}

        {wallet && walletStatusNotice(wallet) && <Notice tone="warn">{walletStatusNotice(wallet)!}</Notice>}
        {wallet && wallet.isLow && wallet.status === 'Active' && (
          <Notice tone="warn">
            {`${wallet.ownerName}'s balance is below ${walletMoney(wallet.lowBalanceAlert, wallet.currency)}. Top up so meals and purchases are not billed to the account.`}
          </Notice>
        )}

        {wallet && !mine.onlineTopUp && (
          <View style={styles.officeBox} testID="wallet-pay-at-office">
            <Text style={styles.officeTitle}>Pay at the school office</Text>
            <Text style={styles.officeText}>
              Top up at the school office with cash or card - quote the Payer ID{' '}
              <Text style={styles.mono}>{wallet.payerId}</Text>. Money you pay in shows here straight away.
            </Text>
          </View>
        )}

        {statementError && <Notice tone="error" onClose={() => setStatementError(null)}>{statementError}</Notice>}
        {openId && !statement && !statementError && <LoadingSpinner />}
        {statement && <Statement statement={statement} styles={styles} colors={colors} onBlockCard={setBlockCardId} />}
      </>
    );
  })();

  const content = (
    <>
      <Segmented<Tab>
        options={[{ value: 'wallet', label: 'Wallet' }, { value: 'pay', label: 'Pay & top up' }]}
        value={tab}
        onChange={setTab}
      />
      {body}
      <View style={{ height: 32 }} />
    </>
  );

  return (
    <>
      {Platform.OS === 'web' ? (
        <View>{content}</View>
      ) : (
        <ScrollView
          showsVerticalScrollIndicator={false}
          refreshControl={tab === 'wallet' ? <RefreshControl refreshing={refreshing} onRefresh={refresh} /> : undefined}
        >
          {content}
        </ScrollView>
      )}

      {topUpFor && (
        <TopUpModal
          wallet={topUpFor}
          minTopUp={minTopUp}
          maxBalance={maxBalance}
          onClose={() => setTopUpFor(null)}
          onDone={async message => {
            setTopUpFor(null);
            setToast({ message, type: 'success' });
            await load();
            if (openId) { await loadStatement(openId); }
          }}
        />
      )}

      <Modal visible={!!blockCardId} transparent animationType="fade" onRequestClose={() => setBlockCardId(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <Text style={styles.modalTitle}>Block this card?</Text>
            <Text style={styles.modalText}>
              Block this card now? It stops working at once; the school can issue a new one.
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setBlockCardId(null)}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: colors.danger }]} onPress={blockCard} testID="confirm-block-card">
                <Text style={styles.primaryBtnText}>Block card</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <BusyOverlay visible={busy} />
      <Toast
        visible={!!toast}
        message={toast?.message ?? ''}
        type={toast?.type ?? 'success'}
        onDismiss={() => setToast(null)}
      />
    </>
  );
}

/**
 * The child's balance, drawn by the shared customer kit (owner, 19 Oct 2026: one design in every
 * app). Teal when all is well, gold when low, slate when frozen or closed.
 */
function WalletBalance({ wallet, canTopUp, onTopUp }: { wallet: Wallet; canTopUp: boolean; onTopUp: () => void }) {
  const colors = wallet.status !== 'Active' ? ['#475569', '#64748B']
    : wallet.isLow ? [tokens.tones.gold.c, tokens.tones.gold.c2]
    : [tokens.tones.teal.c, tokens.tones.teal.c2];
  const meta = [
    `Payer ID ${wallet.payerId}`,
    wallet.status !== 'Active' ? walletStatusChip(wallet).label : null,
    wallet.pointsBalance > 0 ? `${wallet.pointsBalance} points (${walletMoney(wallet.pointsValue, wallet.currency)})` : null,
    wallet.unpaidBillsTotal > 0 ? `${walletMoney(wallet.unpaidBillsTotal, wallet.currency)} unpaid` : null,
  ].filter(Boolean).join(' · ');
  return (
    <View style={{ marginBottom: 12 }}>
      <KitBalanceCard testID={`balance-${wallet.id}`} colors={colors}
        label={`${wallet.ownerName}${wallet.classOrRole ? ` · ${wallet.classOrRole}` : ''}`}
        amount={wallet.cashBalance} currency={wallet.currency} meta={meta}
        actions={canTopUp ? [{ label: 'Top up', onPress: onTopUp, primary: true, testID: `topup-${wallet.id}` }] : []} />
    </View>
  );
}

function Statement({ statement, styles, colors, onBlockCard }: {
  statement: WalletStatement;
  styles: ReturnType<typeof createStyles>;
  colors: ThemeColors;
  onBlockCard: (cardId: string) => void;
}) {
  const { wallet, entries, openBills, cards } = statement;
  const cur = wallet.currency;
  const show = (pocket: string, n: number) => (pocket === 'POINTS' ? `${n} pts` : walletMoney(n, cur));
  // The line a parent tapped: its full details in a popup (the list cuts long lines short).
  const [openEntry, setOpenEntry] = useState<WalletEntry | null>(null);

  return (
    <>
      {openBills.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHead}>
            <Text style={styles.sectionTitle}>Unpaid bills</Text>
            <Text style={[styles.sectionAside, { color: colors.danger }]}>
              {walletMoney(openBills.reduce((t, b) => t + b.amount, 0), cur)}
            </Text>
          </View>
          {openBills.map(b => (
            <View key={b.id} style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.rowTitle}>{b.description ?? b.sourceRef}</Text>
                <Text style={styles.rowSub}>{[stamp(b.createdAt), b.invoiceNumber].filter(Boolean).join(' · ')}</Text>
              </View>
              <Text style={styles.rowAmount}>{walletMoney(b.amount, cur)}</Text>
            </View>
          ))}
          <Text style={styles.small}>Unpaid bills are paid from the next top-up, or at the school office.</Text>
        </View>
      )}

      <View style={styles.section}>
        <View style={styles.sectionHead}>
          <Text style={styles.sectionTitle}>Transactions</Text>
          <Text style={styles.sectionAside}>{entries.length} {entries.length === 1 ? 'line' : 'lines'}</Text>
        </View>
        {entries.length === 0 && <Text style={styles.small}>Nothing has happened in this wallet yet.</Text>}
        {entries.map(e => {
          const incoming = e.direction === 'CR';
          return (
            <TouchableOpacity key={e.id} style={styles.row} onPress={() => setOpenEntry(e)} activeOpacity={0.6}
              accessibilityRole="button" accessibilityHint="Opens the full details of this transaction" testID={`entry-${e.id}`}>
              <View style={[styles.dirIcon, { backgroundColor: incoming ? colors.successLight : colors.dangerLight }]}>
                <FeatherIcon name={incoming ? 'arrow-down-left' : 'arrow-up-right'} size={14} color={incoming ? colors.success : colors.danger} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.rowTitle}>
                  {walletKindLabel(e.kind)}{e.pocket === 'POINTS' ? ' · points' : ''}
                </Text>
                <Text style={styles.rowSub} numberOfLines={2}>
                  {[stamp(e.createdAt), e.description ?? e.sourceRef, e.reason].filter(Boolean).join(' · ')}
                </Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={[styles.rowAmount, { color: incoming ? colors.success : colors.danger }]}>
                  {incoming ? '+' : '-'}{show(e.pocket, e.amount)}
                </Text>
                <Text style={styles.rowSub}>{show(e.pocket, e.balanceAfter)}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      <Modal visible={!!openEntry} transparent animationType="fade" onRequestClose={() => setOpenEntry(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modal, styles.entryModal]} testID="entry-details">
            <Text style={styles.modalTitle}>Transaction details</Text>
            <ScrollView style={styles.entryScroll}>
              {openEntry && entryDetails(openEntry, cur).map(d => (
                <View key={d.label} style={styles.entryRow}>
                  <Text style={styles.entryLabel}>{d.label}</Text>
                  <Text style={styles.entryValue} selectable>{d.value}</Text>
                </View>
              ))}
            </ScrollView>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setOpenEntry(null)} testID="close-entry-details">
                <Text style={styles.secondaryBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {cards.length > 0 && (
        <View style={styles.section}>
          <View style={styles.sectionHead}><Text style={styles.sectionTitle}>Cards</Text></View>
          {cards.map(c => (
            <View key={c.id} style={styles.row}>
              <FeatherIcon name="credit-card" size={18} color={colors.textSecondary} style={{ marginRight: 10 }} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.rowTitle, styles.mono]}>{c.cardSerialMasked ?? c.type}</Text>
                <Text style={styles.rowSub}>
                  {[`${c.status}`, `issued ${stamp(c.issuedAt)}`, c.lastUsedAt ? `last used ${stamp(c.lastUsedAt)}` : null, c.reason]
                    .filter(Boolean).join(' · ')}
                </Text>
              </View>
              {c.status === 'Active' && (
                <TouchableOpacity style={styles.blockBtn} onPress={() => onBlockCard(c.id)} testID={`block-card-${c.id}`}>
                  <Text style={styles.blockBtnText}>Block</Text>
                </TouchableOpacity>
              )}
            </View>
          ))}
          <Text style={styles.small}>Lost a card? Block it here and it stops working at once.</Text>
        </View>
      )}
    </>
  );
}

function TopUpModal({ wallet, minTopUp, maxBalance, onClose, onDone }: {
  wallet: Wallet;
  minTopUp: number;
  maxBalance: number;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const cur = wallet.currency;
  const choices = topUpChoices(minTopUp);
  const [amount, setAmount] = useState(String(choices[0]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const problem = topUpProblem(amount, wallet.cashBalance, minTopUp, maxBalance, cur, false);

  const pay = async () => {
    setTouched(true);
    if (problem || busy) { return; }
    const n = Number(amount);
    setBusy(true);
    setError(null);
    try {
      const started = await walletAPI.startTopUp(wallet.id, n);
      const callback = await openCheckout(started.provider, started.checkoutPayload, `Wallet top-up for ${wallet.ownerName}`);
      await walletAPI.confirmTopUp(started.intentId, callback);
      onDone(`${walletMoney(n, cur)} added to ${wallet.ownerName}'s wallet.`);
    } catch (err) {
      if (isCheckoutCancelled(err)) { setError('Payment cancelled - nothing was taken.'); }
      // The server credits a paid top-up on its own if this screen never hears back.
      else { setError(paymentErrorText(err, 'The top-up did not finish. If money was taken it will reach the wallet shortly.')); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={busy ? () => undefined : onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modal}>
          <View style={styles.modalHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.modalTitle}>Top up</Text>
              <Text style={styles.small}>{wallet.ownerName} · {wallet.payerId}</Text>
            </View>
            {!busy && (
              <TouchableOpacity onPress={onClose} accessibilityLabel="Close">
                <FeatherIcon name="x" size={22} color={colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>

          {error && <Notice tone="error" onClose={() => setError(null)}>{error}</Notice>}

          <Text style={styles.label}>Quick amounts</Text>
          <View style={styles.amountRow}>
            {choices.map(v => {
              const on = Number(amount) === v;
              return (
                <TouchableOpacity
                  key={v}
                  style={[styles.amountChip, on && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                  onPress={() => setAmount(String(v))}
                >
                  <Text style={[styles.amountChipText, on && { color: '#FFF' }]}>{walletMoney(v, cur)}</Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Text style={styles.label}>Amount ({cur})</Text>
          <TextInput
            style={[styles.input, touched && problem ? { borderColor: colors.danger } : null]}
            value={amount}
            onChangeText={v => { setAmount(v.replace(/[^0-9.]/g, '')); setTouched(true); }}
            keyboardType="decimal-pad"
            placeholder="Or type any amount"
            placeholderTextColor={colors.textMuted}
            testID="topup-amount"
          />
          {touched && problem
            ? <Text style={styles.fieldError}>{problem}</Text>
            : <Text style={styles.small}>
                {[minTopUp > 0 ? `Smallest top-up ${walletMoney(minTopUp, cur)}` : null,
                  maxBalance > 0 ? `a wallet holds up to ${walletMoney(maxBalance, cur)}` : null].filter(Boolean).join(' · ') || 'Or type any amount.'}
              </Text>}

          {wallet.unpaidBillsTotal > 0 && (
            <View style={{ marginTop: 10 }}>
              <Notice tone="warn">{`${walletMoney(wallet.unpaidBillsTotal, cur)} of unpaid bills are paid from this first.`}</Notice>
            </View>
          )}

          {busy ? (
            <LoadingSpinner size="small" />
          ) : (
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={onClose}>
                <Text style={styles.secondaryBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryBtn, !!problem && { opacity: 0.5 }]}
                onPress={pay}
                disabled={!!problem}
                testID="pay-topup"
              >
                <FeatherIcon name="credit-card" size={14} color="#FFF" />
                <Text style={styles.primaryBtnText}>{`Pay ${walletMoney(Number(amount) || 0, cur)}`}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  forChild: { fontSize: 13, color: colors.textSecondary, marginBottom: 12 },
  empty: { alignItems: 'center', padding: 28, backgroundColor: colors.card, borderRadius: 14, borderWidth: 1, borderColor: colors.divider },
  emptyIcon: { width: 44, height: 44, borderRadius: 22, backgroundColor: colors.primaryLight, alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  emptyTitle: { fontSize: 14, fontWeight: '700', color: colors.textPrimary, textAlign: 'center' },
  emptySub: { fontSize: 12.5, color: colors.textSecondary, marginTop: 4, textAlign: 'center' },
  officeBox: { backgroundColor: colors.warningLight, borderRadius: 12, padding: 14, marginBottom: 12 },
  officeTitle: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary, marginBottom: 4 },
  officeText: { fontSize: 12.5, lineHeight: 18, color: colors.textPrimary },
  mono: { fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace', fontWeight: '700' },
  section: { backgroundColor: colors.card, borderRadius: 14, borderWidth: 1, borderColor: colors.divider, padding: 14, marginBottom: 12 },
  sectionHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary },
  sectionAside: { fontSize: 12, fontWeight: '700', color: colors.textSecondary },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: colors.divider, gap: 10 },
  rowTitle: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  rowSub: { fontSize: 11.5, color: colors.textSecondary, marginTop: 2 },
  rowAmount: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary },
  dirIcon: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  small: { fontSize: 11.5, color: colors.textSecondary, marginTop: 6, lineHeight: 16 },
  blockBtn: { borderWidth: 1, borderColor: colors.danger, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6 },
  blockBtnText: { color: colors.danger, fontSize: 12, fontWeight: '800' },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, justifyContent: 'center', alignItems: 'center', padding: 20 },
  modal: { backgroundColor: colors.card, borderRadius: 16, padding: 18, width: '100%', maxWidth: 420 },
  modalHeader: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12 },
  modalTitle: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  modalText: { fontSize: 13, color: colors.textSecondary, lineHeight: 19, marginTop: 8 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  entryModal: { maxHeight: '85%' },
  entryScroll: { marginTop: 8 },
  entryRow: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.border },
  entryLabel: { fontSize: 11.5, fontWeight: '700', color: colors.textSecondary, marginBottom: 2 },
  entryValue: { fontSize: 13.5, color: colors.textPrimary, lineHeight: 19 },
  label: { fontSize: 12, fontWeight: '700', color: colors.textSecondary, marginTop: 6, marginBottom: 6 },
  amountRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 6 },
  amountChip: { borderWidth: 1, borderColor: colors.border, borderRadius: 999, paddingHorizontal: 14, paddingVertical: 7, backgroundColor: colors.surface },
  amountChipText: { fontSize: 12.5, fontWeight: '700', color: colors.textPrimary },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 15, color: colors.textPrimary, backgroundColor: colors.surface },
  fieldError: { fontSize: 12, color: colors.danger, marginTop: 6, fontWeight: '600' },
  primaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 11 },
  primaryBtnText: { color: '#FFF', fontSize: 13.5, fontWeight: '800' },
  secondaryBtn: { borderWidth: 1, borderColor: colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 11 },
  secondaryBtnText: { color: colors.textPrimary, fontSize: 13.5, fontWeight: '700' },
});

export const walletStyles = createStyles;
